import SparkMD5 from 'spark-md5';
import Constants from '../submodules/logictry_config/constants';
import AppConstants from '../submodules/logictry_config/s3_config.json';
import Server from '../services/server';
import UserAccount from '../services/UserAccount';
import Company from '../services/Company';

export {
  getClipboardImageFiles,
  MAX_PASTE_IMAGE_BYTES,
} from '../submodules/logictry_wysiwyg/src/utils/imagePaste';

import {
  getClipboardImageFiles,
  MAX_PASTE_IMAGE_BYTES,
} from '../submodules/logictry_wysiwyg/src/utils/imagePaste';

/**
 * Default S3 signing context for user/company asset library uploads.
 * @returns {{ s3Path: string, signingUrl: string } | null}
 */
export function getDefaultAssetUploadContext() {
  if (!UserAccount.isLoggedIn || !UserAccount.account?._id) return null;
  if (Company.company?._id) {
    return {
      s3Path: `${Company.company._id}/${UserAccount.account._id}/`,
      signingUrl: `/v1${AppConstants.S3.signingUrls.companyfiles}`,
    };
  }
  return {
    s3Path: `${UserAccount.account._id}/`,
    signingUrl: `/v1${AppConstants.S3.signingUrls.userfiles}`,
  };
}

/**
 * Signing context for LogicBase stories (no asset library row).
 * @returns {{ s3Path: string, signingUrl: string } | null}
 */
export function getStoriesUploadContext() {
  if (!UserAccount.isLoggedIn || !UserAccount.account?._id) return null;
  return {
    s3Path: `${UserAccount.account._id}/stories/`,
    signingUrl: `/v1${AppConstants.S3.signingUrls.userfiles}`,
  };
}

/**
 * Signing context for messenger attachments (no asset library row).
 * @param {{ conversationId?: string, collectionId?: string }} params
 */
export function getMessengerUploadContext({ conversationId, collectionId, contentId } = {}) {
  if (!UserAccount.isLoggedIn || !UserAccount.account?._id) return null;
  if (!conversationId && !collectionId && !contentId) return null;
  const accountId = UserAccount.account._id;
  if (Company.company?._id) {
    return {
      s3Path: `${Company.company._id}/${accountId}/messenger/`,
      signingUrl: `/v1${AppConstants.S3.signingUrls.companyfiles}`,
      conversationId: conversationId || undefined,
      collectionId: collectionId || undefined,
      contentId: contentId || undefined,
    };
  }
  return {
    s3Path: `${accountId}/messenger/`,
    signingUrl: `/v1${AppConstants.S3.signingUrls.userfiles}`,
    conversationId: conversationId || undefined,
    collectionId: collectionId || undefined,
    contentId: contentId || undefined,
  };
}

/**
 * Default imagePaste props for WysiwygWidget when asset upload is available.
 * Host supplies upload via uploadFile; the submodule never imports platform code.
 * @param {object} [overrides]
 * @returns {object|null}
 */
export function getWysiwygImagePasteOptions(overrides = {}) {
  if (!UserAccount.isLoggedIn) return null;
  const uploadContext = overrides.uploadContext ?? getDefaultAssetUploadContext();
  if (!uploadContext && !overrides.uploadContext) return null;
  const mode = overrides.mode || 'asset';
  const context = overrides.uploadContext ?? uploadContext;
  const { uploadFile: uploadFileOverride, ...rest } = overrides;
  return {
    enabled: true,
    mode,
    uploadContext: context,
    ...rest,
    uploadFile: uploadFileOverride || (async (file) => {
      const { url } = await uploadClipboardImage(file, { mode, context });
      return url;
    }),
  };
}

export function hasClipboardImage(event) {
  return getClipboardImageFiles(event).length > 0;
}

export function formatImageForSpreadsheetCell(url, { rich = false } = {}) {
  if (!url) return '';
  const IMG_STYLE = 'max-width:100%;height:auto;';
  return rich
    ? `<img src="${url}" alt="" style="${IMG_STYLE}" />`
    : url;
}

/** Messenger stores plain text; one image URL per line. */
export function formatImageForMessengerText(url, existingText = '') {
  if (!url) return existingText;
  const line = url.trim();
  if (!existingText?.trim()) return `${line}\n`;
  return `${existingText.replace(/\s+$/, '')}\n${line}\n`;
}

/** Split stored messenger text into image URLs and caption (legacy URL-in-text messages). */
export function splitMessengerMessageContent(text) {
  if (!text) return { imageUrls: [], caption: '' };
  const imageUrls = [];
  const textLines = [];
  text.split('\n').forEach((line) => {
    if (isMessengerImageUrl(line)) {
      imageUrls.push(line.trim());
    } else if (line) {
      textLines.push(line);
    }
  });
  return {
    imageUrls,
    caption: textLines.join('\n').trim(),
  };
}

/** Normalize attachment payloads from API / websocket ({ url, type } or legacy strings). */
export function normalizeMessengerAttachments(raw) {
  if (!raw?.length) return [];
  return raw.map((item) => {
    if (typeof item === 'string') {
      return item.trim() ? { url: item.trim(), type: 'image' } : null;
    }
    const url = item?.url || item?.u;
    if (!url || typeof url !== 'string') return null;
    return {
      url: url.trim(),
      type: item?.type || item?.ty || 'image',
    };
  }).filter((a) => a?.url);
}

/**
 * Resolve images + caption for display/send.
 * Prefers explicit attachments; falls back to URL lines embedded in text (legacy).
 */
export function resolveMessengerMessageContent({ text, attachments } = {}) {
  const normalized = normalizeMessengerAttachments(attachments);
  if (normalized.length) {
    return {
      imageUrls: normalized.map((a) => a.url),
      caption: (text || '').trim(),
      attachments: normalized,
    };
  }
  const legacy = splitMessengerMessageContent(text);
  return {
    ...legacy,
    attachments: legacy.imageUrls.map((url) => ({ url, type: 'image' })),
  };
}

/** @deprecated Use separate text + attachments when sending. Kept for legacy callers. */
export function buildMessengerMessageText(text, attachmentUrls = []) {
  const trimmedText = (text || '').trim();
  const urls = attachmentUrls.map((u) => u?.trim()).filter(Boolean);
  if (!urls.length) return trimmedText;
  const urlBlock = urls.join('\n');
  if (!trimmedText) return `${urlBlock}\n`;
  return `${urlBlock}\n${trimmedText}\n`;
}

/** True when a messenger line should render as an inline image. */
export function isMessengerImageUrl(line) {
  if (!line || typeof line !== 'string') return false;
  const trimmed = line.trim();
  if (!/^https?:\/\//i.test(trimmed)) return false;
  return /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(trimmed)
    || /logictryuserfiles|logictrycompanyfiles|amazonaws\.com/i.test(trimmed);
}

function hashFilename(file) {
  const base = file.name || 'paste.png';
  return SparkMD5.hash(`${Date.now()}-${base}`);
}

function putFile(signed, file) {
  return new Promise((resolve, reject) => {
    if (!signed?.signedUrl) {
      reject(new Error('No signed URL'));
      return;
    }
    const request = new XMLHttpRequest();
    request.open('PUT', signed.signedUrl, true);
    request.setRequestHeader('Content-Disposition', 'inline');
    request.setRequestHeader('Content-Type', file.type || 'image/png');
    request.onreadystatechange = () => {
      if (request.readyState === 4) {
        if (request.status >= 200 && request.status < 300) resolve(signed);
        else reject(new Error('Upload failed'));
      }
    };
    request.send(file);
  });
}

function signedUrlToPublicUrl(signed) {
  return signed.signedUrl?.split('?')[0] || signed.url || '';
}

async function getAssetSignedUpload(file, context) {
  const ctx = { ...getDefaultAssetUploadContext(), ...context };
  if (!ctx?.s3Path || !ctx?.signingUrl) throw new Error('NotAuthorized');
  const uri = `${Constants.logictryOrigin}${ctx.signingUrl}?objectName=${hashFilename(file)}/${encodeURIComponent(file.name || 'paste.png')}&contentType=${encodeURIComponent(file.type || 'image/png')}&path=${ctx.s3Path}`;
  const result = await Server.__request({ uri, method: 'GET', json: true });
  return result?.body;
}

async function getMessengerSignedUpload(file, context) {
  if (!context?.s3Path || !context?.signingUrl) throw new Error('NotAuthorized');
  const params = new URLSearchParams({
    objectName: `${hashFilename(file)}/${encodeURIComponent(file.name || 'paste.png')}`,
    contentType: file.type || 'image/png',
    path: context.s3Path,
  });
  if (context.conversationId) params.set('conversationId', context.conversationId);
  if (context.collectionId) params.set('collectionId', context.collectionId);
  const uri = `${Constants.logictryOrigin}${context.signingUrl}?${params.toString()}`;
  const result = await Server.__request({ uri, method: 'GET', json: true });
  return result?.body;
}

/**
 * Upload a pasted clipboard image.
 * @param {File} file
 * @param {{ mode?: 'asset'|'messenger', context?: object }} [options]
 * @returns {Promise<{ url: string }>}
 */
export default async function uploadClipboardImage(file, { mode = 'asset', context } = {}) {
  if (!UserAccount.isLoggedIn) throw new Error('NotAuthorized');
  if (!file || file.size > MAX_PASTE_IMAGE_BYTES) throw new Error('File too large');
  if (!file.type?.startsWith('image/')) throw new Error('Unsupported file type');

  const signed = mode === 'messenger'
    ? await getMessengerSignedUpload(file, context)
    : await getAssetSignedUpload(file, context);

  if (signed?.error) throw new Error(signed.error);
  await putFile(signed, file);
  const url = signedUrlToPublicUrl(signed);
  if (!url) throw new Error('Upload returned no URL');
  return { url };
}
